import React, { useCallback, useEffect, useState } from 'react';
import { useApp } from '@/context/AppContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import api, { errMsg } from '@/lib/api';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { ShieldCheck, Fingerprint, Smartphone, Trash2, Loader2, KeyRound } from 'lucide-react';
import {
  isMobile, isQuickLoginAvailable, enrollQuickLogin, setQuickHint,
} from '@/lib/webauthn';

const fmt = (s) => {
  if (!s) return '—';
  try { return new Date(s).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' }); }
  catch (e) { return '—'; }
};

export const SecurityPanel = () => {
  const { user } = useApp();
  const isManagerUp = user?.role === 'OWNER' || user?.role === 'MANAGER' || user?.owner_access;
  const isOwner = user?.role === 'OWNER' || user?.owner_access;
  const allowBiometric = !!user?.allow_biometric;

  const [avail, setAvail] = useState(false);
  const [mine, setMine] = useState([]);
  const [admin, setAdmin] = useState([]);
  const [loading, setLoading] = useState(true);
  const [enrolling, setEnrolling] = useState(false);
  const [revokeTarget, setRevokeTarget] = useState(null); // { scope:'self'|'admin', id, label }

  useEffect(() => { if (isMobile()) isQuickLoginAvailable().then(setAvail); }, []);

  const loadMine = useCallback(async () => {
    try { setMine((await api.get('/auth/webauthn/credentials')).data.data.credentials || []); }
    catch (e) { /* ignore */ }
  }, []);
  const loadAdmin = useCallback(async () => {
    if (!isManagerUp) return;
    try { setAdmin((await api.get('/auth/webauthn/admin/credentials')).data.data.credentials || []); }
    catch (e) { /* ignore */ }
  }, [isManagerUp]);

  useEffect(() => {
    (async () => { setLoading(true); await Promise.all([loadMine(), loadAdmin()]); setLoading(false); })();
  }, [loadMine, loadAdmin]);

  const enroll = async () => {
    if (enrolling) return;
    setEnrolling(true);
    try {
      const label = /iPhone|iPad|iPod/i.test(navigator.userAgent) ? 'iPhone'
        : /Android/i.test(navigator.userAgent) ? 'Android phone' : 'This device';
      await enrollQuickLogin(label);
      setQuickHint(user);
      toast.success('Quick Login enabled on this device.');
      await loadMine(); await loadAdmin();
    } catch (err) {
      const name = err?.name;
      if (name === 'NotAllowedError' || name === 'AbortError') toast.error('Setup was cancelled.');
      else toast.error(errMsg(err) || 'Could not enable Quick Login here.');
    } finally { setEnrolling(false); }
  };

  const doRevoke = async () => {
    const t = revokeTarget;
    if (!t) return;
    try {
      const url = t.scope === 'admin'
        ? `/auth/webauthn/admin/credentials/${t.id}/revoke`
        : `/auth/webauthn/credentials/${t.id}/revoke`;
      await api.post(url);
      toast.success('Device removed from Quick Login.');
      setRevokeTarget(null);
      await loadMine(); await loadAdmin();
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="rounded-2xl border border-border bg-card p-4" data-testid="security-panel">
      <div className="mb-1 flex items-center gap-2"><ShieldCheck className="h-4 w-4" /><p className="text-sm font-semibold">Security &amp; Quick Login</p></div>
      <p className="mb-3 text-xs text-muted-foreground">Manage biometric Quick Login (Face ID / Touch ID / fingerprint) for your devices. Your biometrics never leave your phone.</p>

      {/* Enroll this device */}
      {allowBiometric ? (
        isMobile() && avail ? (
          <Button onClick={enroll} disabled={enrolling} className="h-11 w-full rounded-xl" data-testid="security-enroll-device">
            {enrolling ? <span className="flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" />Setting up…</span>
              : <span className="flex items-center gap-2"><Fingerprint className="h-4 w-4" />Enable Quick Login on this device</span>}
          </Button>
        ) : (
          <p className="rounded-lg bg-secondary/40 p-2.5 text-xs text-muted-foreground" data-testid="security-enroll-unavailable">
            Quick Login can be enabled from a mobile device that supports Face ID / Touch ID / fingerprint. On desktop, keep using your username and password.
          </p>
        )
      ) : (
        <p className="rounded-lg bg-secondary/40 p-2.5 text-xs text-muted-foreground" data-testid="security-not-allowed">
          Biometric Quick Login isn't enabled for your account. Ask an Owner to turn on “Allow Biometric Quick Login” in Users &amp; Access.
        </p>
      )}

      {/* My devices */}
      <div className="mt-4">
        <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">My devices</p>
        {loading ? (
          <div className="flex items-center gap-2 text-xs text-muted-foreground"><Loader2 className="h-3.5 w-3.5 animate-spin" />Loading…</div>
        ) : mine.length === 0 ? (
          <p className="text-xs text-muted-foreground" data-testid="security-no-devices">No devices registered yet.</p>
        ) : (
          <div className="space-y-2">
            {mine.map((c) => (
              <div key={c.id} className="flex items-center justify-between rounded-xl border border-border p-3" data-testid={`security-device-${c.id}`}>
                <div className="flex min-w-0 items-center gap-2">
                  <Smartphone className="h-4 w-4 shrink-0 text-muted-foreground" />
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium">{c.label || 'Mobile device'}</p>
                    <p className="truncate text-xs text-muted-foreground">Added {fmt(c.created_at)}{c.last_used ? ` · Last used ${fmt(c.last_used)}` : ''}</p>
                  </div>
                </div>
                <button onClick={() => setRevokeTarget({ scope: 'self', id: c.id, label: c.label })}
                  className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-destructive hover:bg-destructive/10"
                  data-testid={`security-revoke-${c.id}`}><Trash2 className="h-4 w-4" /></button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Admin: all registered devices */}
      {isManagerUp && (
        <div className="mt-4 border-t border-border pt-4">
          <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">All registered devices (admin)</p>
          {admin.length === 0 ? (
            <p className="text-xs text-muted-foreground" data-testid="security-admin-empty">No Quick Login devices registered across users.</p>
          ) : (
            <div className="space-y-2">
              {admin.map((c) => (
                <div key={c.id} className="flex items-center justify-between rounded-xl border border-border p-3" data-testid={`security-admin-device-${c.id}`}>
                  <div className="flex min-w-0 items-center gap-2">
                    <Smartphone className="h-4 w-4 shrink-0 text-muted-foreground" />
                    <div className="min-w-0">
                      <p className="truncate text-sm font-medium">{c.username} <span className="text-xs font-normal text-muted-foreground">· {c.label || 'Mobile device'}</span></p>
                      <p className="truncate text-xs text-muted-foreground">Added {fmt(c.created_at)}{c.last_used ? ` · Last used ${fmt(c.last_used)}` : ''}</p>
                    </div>
                  </div>
                  <button onClick={() => setRevokeTarget({ scope: 'admin', id: c.id, label: `${c.username} · ${c.label || ''}` })}
                    className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-destructive hover:bg-destructive/10"
                    data-testid={`security-admin-revoke-${c.id}`}><Trash2 className="h-4 w-4" /></button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {isOwner && <OwnerPinSection />}

      <AlertDialog open={!!revokeTarget} onOpenChange={(o) => { if (!o) setRevokeTarget(null); }}>
        <AlertDialogContent data-testid="security-revoke-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Remove this device?</AlertDialogTitle>
            <AlertDialogDescription>
              {revokeTarget?.label ? <b>{revokeTarget.label}</b> : 'This device'} will no longer be able to use Quick Login. It can be re-enrolled later from the device.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={doRevoke} className="bg-destructive text-destructive-foreground hover:bg-destructive/90" data-testid="security-revoke-confirm">Remove device</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

// Owner Override PIN setup — a separate PIN that lets the Owner manage users from
// any device's login screen (Owner / Admin Access). Stored server-side hashed only.
const OwnerPinSection = () => {
  const [enabled, setEnabled] = useState(false);
  const [loading, setLoading] = useState(true);
  const [pw, setPw] = useState('');
  const [pin, setPin] = useState('');
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(false);

  const load = useCallback(async () => {
    try { setEnabled(!!(await api.get('/owner-override/pin/status')).data.data.enabled); }
    catch (e) { /* ignore */ } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!pw) { toast.error('Enter your own password.'); return; }
    if (!/^\d{4,8}$/.test(pin)) { toast.error('Choose a 4–8 digit PIN.'); return; }
    setBusy(true);
    try {
      await api.post('/owner-override/pin/set', { current_password: pw, pin });
      toast.success('Owner Override PIN saved.');
      setEnabled(true); setEditing(false); setPw(''); setPin('');
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };

  const remove = async () => {
    if (!pw) { toast.error('Enter your own password to remove the PIN.'); return; }
    setBusy(true);
    try {
      await api.post('/owner-override/pin/remove', { current_password: pw });
      toast.success('Owner Override PIN removed.');
      setEnabled(false); setEditing(false); setPw(''); setPin('');
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="mt-4 border-t border-border pt-4" data-testid="owner-pin-section">
      <div className="mb-1 flex items-center gap-2"><KeyRound className="h-4 w-4" /><p className="text-sm font-semibold">Owner Override PIN</p></div>
      <p className="mb-3 text-xs text-muted-foreground">
        Use “Owner / Admin Access” on any device's login screen to manage users with this PIN — without signing in as that employee. Stored securely; never shown again.
      </p>
      {loading ? (
        <div className="flex items-center gap-2 text-xs text-muted-foreground"><Loader2 className="h-3.5 w-3.5 animate-spin" />Loading…</div>
      ) : !editing ? (
        <div className="flex items-center justify-between rounded-xl border border-border p-3">
          <span className={`text-sm font-medium ${enabled ? 'text-foreground' : 'text-muted-foreground'}`} data-testid="owner-pin-status">
            {enabled ? 'PIN is set' : 'No PIN set'}
          </span>
          <Button variant="secondary" className="h-9 rounded-xl" onClick={() => setEditing(true)} data-testid="owner-pin-edit">
            {enabled ? 'Change / Remove' : 'Set PIN'}
          </Button>
        </div>
      ) : (
        <div className="space-y-2 rounded-xl border border-border p-3">
          <div>
            <Label className="text-xs">Confirm your own password</Label>
            <Input type="password" value={pw} onChange={(e) => setPw(e.target.value)} className="mt-1 h-11 rounded-xl" data-testid="owner-pin-password" />
          </div>
          <div>
            <Label className="text-xs">New Override PIN (4–8 digits)</Label>
            <Input type="password" inputMode="numeric" value={pin} onChange={(e) => setPin(e.target.value)} className="mt-1 h-11 rounded-xl tracking-widest" placeholder="••••" data-testid="owner-pin-input" />
          </div>
          <div className="flex flex-wrap gap-2 pt-1">
            <Button className="h-10 flex-1 rounded-xl" onClick={save} disabled={busy} data-testid="owner-pin-save">{busy ? 'Saving…' : 'Save PIN'}</Button>
            {enabled && <Button variant="destructive" className="h-10 rounded-xl" onClick={remove} disabled={busy} data-testid="owner-pin-remove">Remove</Button>}
            <Button variant="ghost" className="h-10 rounded-xl" onClick={() => { setEditing(false); setPw(''); setPin(''); }} disabled={busy}>Cancel</Button>
          </div>
        </div>
      )}
    </div>
  );
};
